import React, { useState } from "react";
import { toast } from "sonner";
import { AlertTriangle } from "lucide-react";
import { api } from "@/lib/api";
import { Btn, Field, Input, Select, Textarea } from "@/components/kit";
import {
  Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter,
} from "@/components/ui/dialog";
import { formatReg } from "@/lib/vda";

// #15 Run-off dispose / return vehicle workflow. MOT expiry < 3 months blocks
// the normal submission with a clear reason.
function monthsUntil(dateStr) {
  if (!dateStr) return null;
  const d = new Date(dateStr);
  if (isNaN(d)) return null;
  return (d.getTime() - Date.now()) / (1000 * 60 * 60 * 24 * 30.4375);
}

export function DisposeVehicleModal({ vehicle, onClose, onCreated }) {
  const [form, setForm] = useState({
    mot_expiry: vehicle?.mot_due_date || "",
    recall_due: vehicle?.open_recall ? "due" : "none",
    contact_name: "", primary_phone: "", primary_email: "",
    secondary_phone: "", secondary_email: "",
    collection_postcode: "", collection_address: "", collection_date: "",
  });
  const [busy, setBusy] = useState(false);
  const set = (k) => (e) => setForm((f) => ({ ...f, [k]: e.target.value }));

  const motMonths = monthsUntil(form.mot_expiry);
  const motBlocked = motMonths !== null && motMonths < 3;
  const canSubmit = form.mot_expiry && form.contact_name && form.primary_phone && form.primary_email
    && form.collection_postcode && form.collection_address && form.collection_date && !motBlocked;

  const submit = async () => {
    setBusy(true);
    try {
      await api.fmDispose({ vehicle_id: vehicle.id, ...form });
      toast.success("Return request submitted to your Venson team.");
      onCreated?.();
    } catch (e) { toast.error(e.message || "Couldn't submit the return request."); }
    finally { setBusy(false); }
  };

  return (
    <Dialog open onOpenChange={(o) => { if (!o) onClose(); }}>
      <DialogContent className="max-w-lg max-h-[85vh] overflow-y-auto" data-testid="dispose-modal">
        <DialogHeader>
          <DialogTitle>Return / dispose vehicle · {formatReg(vehicle?.registration)}</DialogTitle>
        </DialogHeader>
        <div className="space-y-3">
          <Field label="MOT expiry date">
            <Input type="date" value={form.mot_expiry} onChange={set("mot_expiry")} data-testid="dispose-mot" />
          </Field>
          {motBlocked && (
            <div className="rounded-[6px] border border-[hsl(var(--vda-red))]/30 bg-[hsl(var(--vda-red-bg))] px-3 py-2 flex items-start gap-2" data-testid="dispose-mot-block">
              <AlertTriangle className="h-4 w-4 mt-0.5 text-[hsl(var(--vda-red))]" />
              <p className="text-sm text-[hsl(var(--vda-red))]">
                This vehicle has under 3 months' MOT remaining, which is insufficient for the return
                workflow. Please contact your Venson team to arrange the return.
              </p>
            </div>
          )}
          <Field label="Recall position">
            <Select value={form.recall_due} onChange={set("recall_due")} data-testid="dispose-recall">
              <option value="none">No known recall due</option>
              <option value="due">Recall due / outstanding</option>
              <option value="unknown">Not known</option>
            </Select>
          </Field>
          <Field label="Collection / contact name">
            <Input value={form.contact_name} onChange={set("contact_name")} data-testid="dispose-name" />
          </Field>
          <div className="grid grid-cols-2 gap-3">
            <Field label="Collection postcode"><Input value={form.collection_postcode} onChange={set("collection_postcode")} data-testid="dispose-postcode" /></Field>
            <Field label="Requested collection date"><Input type="date" value={form.collection_date} onChange={set("collection_date")} data-testid="dispose-date" /></Field>
          </div>
          <Field label="Full collection address">
            <Textarea rows={2} value={form.collection_address} onChange={set("collection_address")} data-testid="dispose-address" placeholder="Building, street, town" />
          </Field>
          <div className="grid grid-cols-2 gap-3">
            <Field label="Primary phone"><Input value={form.primary_phone} onChange={set("primary_phone")} data-testid="dispose-phone1" inputMode="tel" /></Field>
            <Field label="Primary email"><Input value={form.primary_email} onChange={set("primary_email")} data-testid="dispose-email1" inputMode="email" /></Field>
            <Field label="Secondary phone (optional)"><Input value={form.secondary_phone} onChange={set("secondary_phone")} inputMode="tel" /></Field>
            <Field label="Secondary email (optional)"><Input value={form.secondary_email} onChange={set("secondary_email")} inputMode="email" /></Field>
          </div>
        </div>
        <DialogFooter>
          <Btn variant="outline" onClick={onClose}>Cancel</Btn>
          <Btn onClick={submit} loading={busy} disabled={!canSubmit} data-testid="dispose-submit">Submit return request</Btn>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}
